<template>
  <el-container class="secure v-secure">
    <HeaderAlerts
      :show-in="'admin-panel'"
    />
    <el-container class="secure__content">
      <el-aside
        v-if="!isFullscreen"
        class="aside"
        :color="'nav-primary'"
        :width="'256px'"
      >
        <ui-logo />
        <main-menu />
        <ClusterStatusWidget ref="clusterStatusWidget" />
      </el-aside>
      <el-main
        class="main"
      >
        <slot />

        <bottom-notifications />
        <features-config />
        <telemetry-consent-banner />
        <compute-provisioning-welcome-dialog />
      </el-main>
    </el-container>
    <confirm ref="globalConfirm" />
    <global-dialog ref="globalDialog" />
  </el-container>
</template>

<script lang="ts">
  import { mapGetters } from 'vuex';
  import MainMenu from '@/common/components/MainMenu.vue';
  import UiLogo from '@/common/components/UiLogo.vue';
  import ClusterStatusWidget from '@/common/components/ClusterStatusWidget.vue';
  import FeaturesConfig from '@/common/components/FeaturesConfig.vue';
  import BottomNotifications from '@/common/components/notification-center/BottomNotifications.vue';
  import Confirm from '@/common/shared/Confirm.vue';
  import { mapState, mapStores } from 'pinia';
  import useAuthStore from '@/common/store/pinia/auth';
  import GlobalDialog from '@/common/shared/GlobalDialog.vue';
  import useAppStore from '@/common/store/pinia/app';
  import { defineComponent } from 'vue';
  import TelemetryConsentBanner from '@/common/components/TelemetryConsentBanner.vue';
  import ComputeProvisioningWelcomeDialog from '@/common/components/compute/provisioning/ComputeProvisioningWelcomeDialog.vue';
  import HeaderAlerts from '@/common/components/header-alerts/HeaderAlerts.vue';

  export default defineComponent({
    name: 'SecureLayout',
    components: {
      HeaderAlerts,
      Confirm,
      GlobalDialog,
      BottomNotifications,
      FeaturesConfig,
      MainMenu,
      ClusterStatusWidget,
      UiLogo,
      TelemetryConsentBanner,
      ComputeProvisioningWelcomeDialog
    },
    provide() {
      return {
        globalConfirm: () => this.$refs.globalConfirm,
      };
    },
    mounted() {
      this.$root.$globalConfirm = this.$refs.globalConfirm;

      this.authStore.signInGrafana();
      if (this.$can('read', 'network-reconfiguration-details')) {
        this.$store.$takeFirst('reconfiguration/CHECK_RECONFIGURATION');
      }
    },
    computed: {
      ...mapState(useAppStore, {
        isFullscreen: 'isFullscreen'
      }),
      ...mapStores(useAuthStore),
      ...mapGetters({
        reconfigurationRequired: 'reconfiguration/reconfigurationRequired',
        reconfiguringTask: 'reconfiguration/task'
      }),
      canCheckUpdates() {
        return this.$can('read', 'software_updates');
      }
    },
    watch: {
      reconfigurationRequired(newVal) {
        if (newVal) {
          this.$router.replace({
            name: 'reconfiguration',
            params: {
              operation: this.reconfiguringTask.operation
            }
          });
        }
      },
      canCheckUpdates: {
        immediate: true,
        handler(newVal, prevVal) {
          if (newVal && !prevVal) {
            this.$store.$takeFirst('updates/GET_UPDATES_STATUS');
          }
        }
      }
    }
  });
</script>

<style lang="scss" scoped>
  .secure {
    flex-direction: column;
    overflow: hidden;
    max-height: 100vh;
    height: 100vh;

    &__content {
      flex: 1;
      overflow: hidden;
    }

    .main {
      flex: 1;
      display: flex;
      overflow: hidden;
      height: 100%;

      .sidebar {
        width: $hci-menu-width;
        display: flex;
        flex-flow: column nowrap;
        height: 100%;

        &-bottom {
          margin-top: auto;
          background: $av-nav-primary;
          height: 64px;
        }

        @media only screen and (max-width: $--md) {
          width: 64px;
          overflow: visible;
        }
      }

      .section {
        flex: 1;
        overflow: hidden;
      }
    }

    @media only screen and (max-width: $--md) {
      .menu {
        overflow: visible;
      }
    }
  }

  .aside {
    background: $av-nav-primary;
    overflow: hidden;
  }

</style>
